import { createFileRoute, Link, notFound } from "@tanstack/react-router";

import { SiteFooter, SiteNav } from "@/components/SiteChrome";
import { getPublishedEssayBySlug } from "@/lib/essays.functions";

export const Route = createFileRoute("/garden/$slug")({
  loader: async ({ params }) => {
    const essay = await getPublishedEssayBySlug({ data: { slug: params.slug } });
    if (!essay) throw notFound();
    return essay;
  },
  head: ({ loaderData }) => ({
    meta: loaderData
      ? [
          { title: `${loaderData.title} — Akram Fortas` },
          { name: "description", content: loaderData.subtitle ?? loaderData.title },
          { property: "og:title", content: loaderData.title },
          {
            property: "og:description",
            content: loaderData.subtitle ?? "A note from Akram's digital garden.",
          },
          { property: "og:url", content: `https://akramfortas.com/garden/${loaderData.slug}` },
          { property: "og:type", content: "article" },
        ]
      : [{ title: "Essay — Akram Fortas" }],
    links: loaderData
      ? [{ rel: "canonical", href: `https://akramfortas.com/garden/${loaderData.slug}` }]
      : [],
  }),
  notFoundComponent: () => (
    <div className="min-h-screen bg-background text-foreground">
      <SiteNav />
      <main className="container-prose pt-20 pb-16">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
          Not planted here
        </p>
        <h1 className="mt-6 font-serif text-4xl text-foreground">This essay isn't published.</h1>
        <p className="mt-4 text-muted-foreground">
          Either it's still a seed, or it never took root.
        </p>
        <p className="mt-8">
          <Link to="/garden" className="link-quiet underline underline-offset-4">
            ← Back to the garden
          </Link>
        </p>
      </main>
      <SiteFooter />
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="min-h-screen bg-background p-10 text-foreground">
      <p>Failed to load essay: {error.message}</p>
    </div>
  ),
  component: EssayPage,
});

function EssayPage() {
  const essay = Route.useLoaderData();
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteNav />
      <main className="container-prose pt-20 pb-16 md:pt-28">
        {essay.category && (
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
            {essay.category}
          </p>
        )}
        <h1 className="mt-6 font-serif text-5xl text-balance text-foreground md:text-6xl">
          {essay.title}
        </h1>
        {essay.subtitle && (
          <p className="mt-6 max-w-2xl text-xl text-muted-foreground text-pretty">
            {essay.subtitle}
          </p>
        )}
        {essay.published_at && (
          <p className="mt-8 font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
            {new Date(essay.published_at).toLocaleDateString("en", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </p>
        )}
        <article className="prose prose-neutral mt-12 max-w-none whitespace-pre-wrap font-serif text-lg leading-relaxed text-foreground">
          {essay.body_md}
        </article>
        <p className="mt-20 text-sm text-muted-foreground">
          <Link to="/garden" className="link-quiet underline underline-offset-4">
            ← Back to the garden
          </Link>
        </p>
      </main>
      <SiteFooter />
    </div>
  );
}
